import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import type {
  PresentationAudienceCatalogResponse,
  PresentationCodeSentResponse,
  PresentationCompanySearchResponse,
  PresentationContextResponse,
  PresentationRegistrationData,
  PresentationTourResponse,
} from './models';
import { apiBaseUrl } from 'lib.shared/src/api';

const PRESENTATION_TAG = 'presentation';

/**
 * Raw base query without reauth — the start page is public
 * (token-guarded), so a 401 must never trigger the login redirect.
 * Cookies always ride along: the code check runs on the web session (the
 * backend signs the visitor in), and every call must carry the same one.
 */
const rawBaseQuery = fetchBaseQuery({
  baseUrl: apiBaseUrl,
  credentials: 'include',
});

export const presentationApi = createApi({
  reducerPath: 'presentation',
  baseQuery: rawBaseQuery,
  tagTypes: [PRESENTATION_TAG],
  endpoints: (build) => ({
    /**
     * `surface` names the page behind the request — the backend's first-open
     * event and Slack announcement report it. The arg IS the cache key the
     * mutations below update pessimistically.
     */
    getContext: build.query<PresentationContextResponse, { token: string; surface?: string }>({
      query: ({ token, surface }) => ({
        url: `/presentation/${token}/context`,
        params: surface ? { surface } : undefined,
        headers: { Accept: 'application/json' },
      }),
      providesTags: [{ type: PRESENTATION_TAG, id: 'CONTEXT' }],
    }),

    getTour: build.query<PresentationTourResponse, { token: string; surface?: string }>({
      query: ({ token, surface }) => ({
        url: `/presentation/${token}/tour`,
        params: surface ? { surface } : undefined,
        headers: { Accept: 'application/json' },
      }),
      providesTags: [{ type: PRESENTATION_TAG, id: 'TOUR' }],
    }),

    // the audience step's catalog: sections, sizes, the recommended groups, the pick
    getAudiences: build.query<
      PresentationAudienceCatalogResponse,
      { token: string; surface?: string }
    >({
      query: ({ token, surface }) => ({
        url: `/presentation/${token}/audiences`,
        params: surface ? { surface } : undefined,
        headers: { Accept: 'application/json' },
      }),
      providesTags: [{ type: PRESENTATION_TAG, id: 'AUDIENCES' }],
    }),

    // the audience step — the account exists by now: written to its brief at once
    submitAudiences: build.mutation<
      PresentationContextResponse,
      { token: string; surface: string; audiences: number[] }
    >({
      query: ({ token, surface, audiences }) => ({
        url: `/presentation/${token}/audiences`,
        method: 'POST',
        params: { surface },
        body: { audiences },
        headers: { Accept: 'application/json' },
      }),
      async onQueryStarted({ token, surface }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            presentationApi.util.updateQueryData('getContext', { token, surface }, () => data),
          );
        } catch {
          /* mutation failed — cache stays as is */
        }
      },
    }),

    // the default page's company field: the event's exhibitors by name, website or work email
    searchCompanies: build.query<
      PresentationCompanySearchResponse,
      { token: string; surface?: string; q: string }
    >({
      query: ({ token, surface, q }) => ({
        url: `/presentation/${token}/companies`,
        params: { q, ...(surface ? { surface } : {}) },
        headers: { Accept: 'application/json' },
      }),
    }),

    // the default page's pick — the response IS the context for that company
    pickCompany: build.mutation<
      PresentationContextResponse,
      { token: string; surface: string; company: string }
    >({
      query: ({ token, surface, company }) => ({
        url: `/presentation/${token}/company`,
        method: 'POST',
        params: { surface },
        body: { company },
        headers: { Accept: 'application/json' },
      }),
      async onQueryStarted({ token, surface }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            presentationApi.util.updateQueryData('getContext', { token, surface }, () => data),
          );
        } catch {
          /* mutation failed — cache stays as is */
        }
      },
      // the copy names the company: the tour is read again for it
      invalidatesTags: [{ type: PRESENTATION_TAG, id: 'TOUR' }],
    }),

    // the sign-up's one field: a 6-digit code to the work email
    sendEmailCode: build.mutation<
      PresentationCodeSentResponse,
      { token: string; surface: string; email: string }
    >({
      query: ({ token, surface, email }) => ({
        url: `/presentation/${token}/email-code`,
        method: 'POST',
        params: { surface },
        body: { email },
        headers: { Accept: 'application/json' },
      }),
    }),

    // the code typed — the backend settles the account (made, joined, or waiting
    // for an admin) and signs the visitor in where it is theirs; the response IS the context
    verifyEmailCode: build.mutation<
      PresentationContextResponse,
      { token: string; surface: string; email: string; code: string }
    >({
      query: ({ token, surface, email, code }) => ({
        url: `/presentation/${token}/verify-code`,
        method: 'POST',
        params: { surface },
        body: { email, code },
        headers: { Accept: 'application/json' },
      }),
      async onQueryStarted({ token, surface }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            presentationApi.util.updateQueryData('getContext', { token, surface }, () => data),
          );
        } catch {
          /* mutation failed — cache stays as is */
        }
      },
    }),

    // the sign-up form with Google / LinkedIn (StartSave) and the sign-in link's
    // hand-over: off the flow for now, kept with their endpoints for a return

    registerFromPresentation: build.mutation<
      PresentationContextResponse,
      // surface — the page signing up: a start-page account continues to the desk
      { token: string; data: PresentationRegistrationData; surface?: string }
    >({
      query: ({ token, data, surface }) => ({
        url: `/presentation/${token}/register`,
        method: 'POST',
        body: data,
        params: surface ? { surface } : undefined,
        headers: { Accept: 'application/json' },
      }),
      // the response IS the refreshed (now unlocked) context — update the
      // cache pessimistically instead of discarding it with a refetch
      async onQueryStarted({ token, surface }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            presentationApi.util.updateQueryData(
              'getContext',
              surface ? { token, surface } : { token },
              () => data,
            ),
          );
        } catch {
          /* mutation failed — cache stays as is */
        }
      },
    }),

    // links the user the OAuth popup just authenticated (web session) to the
    // presentation session; 409 = the account already has an organization
    completeOauth: build.mutation<PresentationContextResponse, { token: string; surface?: string }>(
      {
        query: ({ token, surface }) => ({
          url: `/presentation/${token}/complete-oauth`,
          method: 'POST',
          params: surface ? { surface } : undefined,
          headers: { Accept: 'application/json' },
        }),
        async onQueryStarted({ token, surface }, { dispatch, queryFulfilled }) {
          try {
            const { data } = await queryFulfilled;
            dispatch(
              presentationApi.util.updateQueryData(
                'getContext',
                surface ? { token, surface } : { token },
                () => data,
              ),
            );
          } catch {
            /* mutation failed — cache stays as is */
          }
        },
      },
    ),

    // an existing account signed in by the start page's sign-in link takes the
    // reading over (no password asked again); 422 = the account belongs elsewhere
    signInAccount: build.mutation<PresentationContextResponse, { token: string; surface?: string }>(
      {
        query: ({ token, surface }) => ({
          url: `/presentation/${token}/sign-in`,
          method: 'POST',
          params: surface ? { surface } : undefined,
          headers: { Accept: 'application/json' },
        }),
        async onQueryStarted({ token, surface }, { dispatch, queryFulfilled }) {
          try {
            const { data } = await queryFulfilled;
            dispatch(
              presentationApi.util.updateQueryData(
                'getContext',
                surface ? { token, surface } : { token },
                () => data,
              ),
            );
          } catch {
            /* mutation failed — cache stays as is */
          }
        },
      },
    ),
  }),
});

export const {
  useGetContextQuery,
  useGetTourQuery,
  useGetAudiencesQuery,
  useSubmitAudiencesMutation,
  useLazySearchCompaniesQuery,
  usePickCompanyMutation,
  useSendEmailCodeMutation,
  useVerifyEmailCodeMutation,
  useRegisterFromPresentationMutation,
  useCompleteOauthMutation,
  useSignInAccountMutation,
} = presentationApi;
